import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

const badgeVariants = cva(
	"icon-button-base gap-1 font-medium focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none",
	{
		variants: {
			variant: {
				// Shadcn standard variants
				default:
					"bg-primary text-primary-foreground hover:bg-primary/80 border-transparent",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80 border-transparent",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/80 border-transparent",
				outline: "text-foreground border",
				// Custom variants from legacy Badge
				light: "", // Placeholder for compound variants
				solid: "", // Placeholder for compound variants
			},
			size: {
				sm: "px-2 py-0.5 text-xs",
				md: "px-2.5 py-0.5 text-xs font-medium",
				lg: "px-3 py-1 text-sm",
			},
			color: {
				primary: "",
				brand: "", // Alias for primary
				success: "",
				error: "",
				warning: "",
				info: "",
				light: "",
				dark: "",
			},
		},
		compoundVariants: [
			// Light variant combinations
			{
				variant: "light",
				color: "primary",
				class:
					"bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400",
			},
			{
				variant: "light",
				color: "brand",
				class:
					"bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400",
			},
			{
				variant: "light",
				color: "success",
				class:
					"bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500",
			},
			{
				variant: "light",
				color: "error",
				class:
					"bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500",
			},
			{
				variant: "light",
				color: "warning",
				class:
					"bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-orange-400",
			},
			{
				variant: "light",
				color: "info",
				class:
					"bg-blue-light-50 text-blue-light-500 dark:bg-blue-light-500/15 dark:text-blue-light-500",
			},
			{
				variant: "light",
				color: "light",
				class: "bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80",
			},
			{
				variant: "light",
				color: "dark",
				class: "bg-gray-500 text-white dark:bg-white/5 dark:text-white",
			},
			// Solid variant combinations
			{
				variant: "solid",
				color: "primary",
				class: "bg-brand-500 text-white dark:text-white",
			},
			{
				variant: "solid",
				color: "brand",
				class: "bg-brand-500 text-white dark:text-white",
			},
			{
				variant: "solid",
				color: "success",
				class: "bg-success-500 text-white dark:text-white",
			},
			{
				variant: "solid",
				color: "error",
				class: "bg-error-500 text-white dark:text-white",
			},
			{
				variant: "solid",
				color: "warning",
				class: "bg-warning-500 text-white dark:text-white",
			},
			{
				variant: "solid",
				color: "info",
				class: "bg-blue-light-500 text-white dark:text-white",
			},
			{
				variant: "solid",
				color: "light",
				class: "bg-gray-400 text-white dark:bg-white/5 dark:text-white/80",
			},
			{
				variant: "solid",
				color: "dark",
				class: "bg-gray-700 text-white dark:text-white",
			},
		],
		defaultVariants: {
			variant: "default",
			size: "md",
		},
	},
);

export interface BadgeProps
	extends
		Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
		VariantProps<typeof badgeVariants> {
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
}

function Badge({
	className,
	variant,
	size,
	color,
	startIcon,
	endIcon,
	children,
	...props
}: BadgeProps) {
	return (
		<div
			className={cn(badgeVariants({ variant, size, color }), className)}
			{...props}
		>
			{startIcon && (
				<span className="inline-flex items-center">{startIcon}</span>
			)}
			{children}
			{endIcon && <span className="inline-flex items-center">{endIcon}</span>}
		</div>
	);
}

export { Badge };
